/** @typedef {import('./quotaUserTypes.js').QuotaUser} QuotaUser */

export const GENDER_LABELS = {
  1: 'Male',
  2: 'Female',
};

export function formatGender(value) {
  if (value == null) return '—';
  if (typeof value === 'string') return value;
  return GENDER_LABELS[value] ?? String(value);
}

export function getUserCnqTypes(user) {
  const types = [
    ...new Set((user.quota_data ?? []).map((quota) => quota.cnq_type).filter((type) => type != null)),
  ];
  return types.sort((a, b) => Number(a) - Number(b));
}

/** Safe access — null / missing user_termination must not throw. */
export function getUserTermination(user) {
  return user?.user_termination ?? null;
}

export function getTerminationCnqId(user) {
  const termination = getUserTermination(user);
  if (!termination || termination.cnq_id == null) return null;
  return Number(termination.cnq_id);
}

export function isOverQuotaQuota(quota, terminationCnqId) {
  if (!quota) return false;
  if (quota.is_over_quota_reason === true) return true;
  if (terminationCnqId == null || quota.cnq_id == null) return false;
  return Number(quota.cnq_id) === terminationCnqId;
}

/** When termination quota is not in quota_data (e.g. previous study), surface it separately. */
export function getOrphanTerminationQuota(user) {
  const termination = getUserTermination(user);
  const terminationCnqId = getTerminationCnqId(user);
  if (terminationCnqId == null) return null;

  const inQuotaData = (user.quota_data ?? []).some(
    (quota) => Number(quota.cnq_id) === terminationCnqId,
  );
  if (inQuotaData) return null;

  const orphan = termination.quota;
  if (!orphan || typeof orphan !== 'object') {
    return { cnq_id: terminationCnqId, quota_name: null, cnq_type: null };
  }

  return {
    cnq_id: orphan.cnq_id ?? terminationCnqId,
    quota_name: orphan.quota_name ?? null,
    cnq_type: orphan.cnq_type ?? null,
    cnq_audience: orphan.cnq_audience,
    cnq_reach: orphan.cnq_reach,
    is_over_quota_reason: true,
  };
}

/** True when user is in over-quota state (wh_flag 2 or termination / quota markers). */
export function isOverQuotaUser(user) {
  if (user?.wh_flag === 2) return true;

  const termination = getUserTermination(user);
  if (termination?.terminate === true) return true;

  if ((user?.quota_data ?? []).some((quota) => quota.is_over_quota_reason === true)) {
    return true;
  }

  if ((user?.editable_quotas ?? []).some((quota) => quota.is_over_quota_reason === true)) {
    return true;
  }

  return false;
}

export function userHasAssignedQuotas(user) {
  if (Array.isArray(user?.selected_cnq_ids) && user.selected_cnq_ids.length > 0) {
    return true;
  }

  return (user?.editable_quotas ?? []).some((quota) => quota.is_selected === true);
}

function getSortValue(user, key) {
  if (key === 'cnq_type') {
    const types = getUserCnqTypes(user);
    return types.length ? Number(types[0]) : null;
  }
  return user[key];
}

function compareSortValues(aVal, bVal, multiplier) {
  if (aVal == null && bVal == null) return 0;
  if (aVal == null) return 1;
  if (bVal == null) return -1;

  if (typeof aVal === 'string' || typeof bVal === 'string') {
    return String(aVal).localeCompare(String(bVal), undefined, { numeric: true }) * multiplier;
  }

  return (Number(aVal) - Number(bVal)) * multiplier;
}

export function sortUsers(users, sortKey, sortDir) {
  if (!sortKey) return users;

  const list = [...users];
  const multiplier = sortDir === 'asc' ? 1 : -1;

  list.sort((a, b) =>
    compareSortValues(getSortValue(a, sortKey), getSortValue(b, sortKey), multiplier),
  );

  return list;
}

/** Build toast text from revive / bulk-swap batch envelopes. */
export function formatBatchResultToast(message, { successIds, skipped, failed, successLabel }) {
  const successCount = Array.isArray(successIds) ? successIds.length : 0;
  const skippedCount = Array.isArray(skipped) ? skipped.length : 0;
  const failedCount = Array.isArray(failed) ? failed.length : 0;
  const firstFailure = Array.isArray(failed) ? failed[0]?.reason : null;

  const base =
    message ||
    `${successLabel} ${successCount} user${successCount !== 1 ? 's' : ''}`;

  const extras = [
    skippedCount ? `${skippedCount} skipped` : null,
    failedCount ? `${failedCount} failed` : null,
  ].filter(Boolean);

  const text = extras.length ? `${base} (${extras.join(', ')})` : base;
  const isError = successCount === 0 && failedCount > 0;

  return {
    text: isError && firstFailure ? `${text}. ${firstFailure}` : text,
    type: isError ? 'error' : 'success',
  };
}
